import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Trash2, Copy, Mail, BarChart2, Zap, TrendingUp, PlusCircle, MousePointer, UserMinus } from 'lucide-react';
import templatesAPI from '../api/templates';
import analyticsAPI from '../api/analytics';
import CampaignDrawer from '../components/CampaignDrawer.jsx';

const GLASS = {
  background: 'rgba(26,26,26,0.6)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
};

const CAT_COLORS = {
  promo: '#FF1493', newsletter: '#A855F7', announcement: '#FF8C42',
  'thank-you': '#00D9FF', event: '#39FF14', general: '#0080FF',
  welcome: '#00FFC8', followup: '#FFD700',
};

const TAB = (active, color = '#00D9FF') => ({
  padding: '8px 20px', fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
  borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s',
  border: active ? `1px solid ${color}66` : '1px solid transparent',
  background: active ? `${color}18` : 'transparent',
  color: active ? color : 'rgba(255,255,255,0.4)',
});

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [myTemplates, setMyTemplates] = useState([]);
  const [starters, setStarters]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('my-templates');
  const [duplicating, setDuplicating] = useState(null);
  const [stats, setStats]             = useState({ total: 0, published: 0, drafts: 0 });
  const [overview, setOverview]       = useState(null);
  const [campaignTemplate, setCampaignTemplate] = useState(null);

  useEffect(() => {
    Promise.all([
      templatesAPI.getAll({ limit: 100 }).catch(() => null),
      templatesAPI.getStarters().catch(() => null),
      analyticsAPI.getOverview().catch(() => null),
    ]).then(([myRes, starterRes, ovRes]) => {
      const my = myRes?.data?.templates || [];
      const st = starterRes?.data?.templates || starterRes?.data || [];
      setMyTemplates(my);
      setStarters(Array.isArray(st) ? st : []);
      setStats({
        total: my.length,
        published: my.filter(t => t.isPublished).length,
        drafts: my.filter(t => !t.isPublished).length,
      });
      if (ovRes?.data) setOverview(ovRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await templatesAPI.delete(id);
      setMyTemplates(prev => prev.filter(t => t._id !== id));
      setStats(s => ({ ...s, total: s.total - 1 }));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleUseStarter = async (e, id, name) => {
    e.stopPropagation();
    if (duplicating) return;
    setDuplicating(id);
    try {
      const res = await templatesAPI.duplicate(id, `${name} (Copy)`);
      const newId = res.data?.template?._id || res.data?._id;
      if (newId) navigate(`/editor/${newId}`);
      else alert('Failed to get new template ID');
    } catch (err) {
      alert('Failed to use template: ' + err.message);
    } finally {
      setDuplicating(null);
    }
  };

  const displayed = activeTab === 'starters' ? starters : myTemplates;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#fff', padding: '32px 28px', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            ✉️ Email Manager
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>Build, manage and send beautiful emails</p>
        </div>
        <button onClick={() => navigate('/editor')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#FF1493,#A855F7)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.03em' }}>
          <PlusCircle size={16} /> New Template
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 16, marginBottom: 32 }}>
        {[
          { icon: <Mail size={20} />,        label: 'Total Templates', value: stats.total,     color: '#00D9FF' },
          { icon: <Zap size={20} />,         label: 'Published',       value: stats.published, color: '#39FF14' },
          { icon: <Edit2 size={20} />,       label: 'Drafts',          value: stats.drafts,    color: '#FF8C42' },
          { icon: <TrendingUp size={20} />,  label: 'Coming soon',     value: '—',             color: '#A855F7', sub: 'Analytics' },
        ].map(s => (
          <div key={s.label} style={{ ...GLASS, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: `${s.color}18`, border: `1px solid ${s.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color, flexShrink: 0 }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>{s.sub || s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Analytics Summary */}
      <div style={{ ...GLASS, padding: '24px 28px', marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <BarChart2 size={18} color="#A855F7" />
          <span style={{ fontSize: 15, fontWeight: 700 }}>Email Analytics</span>
          <button onClick={() => navigate('/analytics')}
            style={{ marginLeft: 'auto', fontSize: 11, padding: '4px 12px', borderRadius: 20, background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.4)', color: '#A855F7', fontWeight: 700, cursor: 'pointer' }}>
            View Full Dashboard →
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 16 }}>
          {[
            { label: 'Emails Sent',  value: overview ? String(overview.totalSent) : '—',           color: '#00D9FF', icon: <Mail size={14} /> },
            { label: 'Open Rate',    value: overview ? `${overview.openRate}%` : '—',               color: '#39FF14', icon: <TrendingUp size={14} /> },
            { label: 'Click Rate',   value: overview ? `${overview.clickRate}%` : '—',              color: '#FF8C42', icon: <MousePointer size={14} /> },
            { label: 'Unsubscribes', value: overview ? String(overview.totalUnsubscribed) : '—',    color: '#FF1493', icon: <UserMinus size={14} /> },
          ].map(m => (
            <div key={m.label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, color: m.color }}>{m.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: m.color }}>{m.value}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Template tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: 16 }}>
        <button style={TAB(activeTab === 'my-templates', '#FF1493')} onClick={() => setActiveTab('my-templates')}>
          My Templates {myTemplates.length > 0 && `(${myTemplates.length})`}
        </button>
        <button style={TAB(activeTab === 'starters', '#00D9FF')} onClick={() => setActiveTab('starters')}>
          ✨ Starters {starters.length > 0 && `(${starters.length})`}
        </button>
      </div>

      {duplicating && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ ...GLASS, padding: '28px 40px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>⏳</div>
            <p style={{ margin: 0, fontWeight: 700 }}>Creating your template…</p>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(255,255,255,0.3)' }}>Loading templates…</div>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>✉️</div>
          <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
            {activeTab === 'starters' ? 'No starter templates yet' : 'No templates yet'}
          </p>
          <p style={{ margin: '0 0 24px', fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>
            {activeTab === 'starters' ? 'Check back soon' : 'Create your first email template to get started'}
          </p>
          {activeTab === 'my-templates' && (
            <button onClick={() => navigate('/editor')}
              style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#FF1493,#A855F7)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              + Create Template
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 18 }}>
          {displayed.map(tmpl => {
            const cat   = tmpl.category || 'general';
            const color = CAT_COLORS[cat] || '#00D9FF';
            return (
              <div key={tmpl._id}
                onClick={() => tmpl.isStarter
                  ? handleUseStarter({ stopPropagation: () => {} }, tmpl._id, tmpl.name)
                  : navigate(`/editor/${tmpl._id}`)
                }
                style={{ ...GLASS, cursor: 'pointer', overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${color}55`; e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px ${color}22`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.3)'; }}>

                <div style={{ height: 120, background: 'rgba(255,255,255,0.03)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Mail size={32} color={color} opacity={0.4} />
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: color }} />
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
                    {tmpl.isStarter && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(255,216,0,0.15)', border: '1px solid rgba(255,216,0,0.4)', color: '#FFD700' }}>STARTER</span>}
                    {tmpl.isPublished && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.4)', color: '#39FF14' }}>LIVE</span>}
                  </div>
                </div>

                <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{tmpl.name || 'Untitled'}</p>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: `${color}18`, border: `1px solid ${color}44`, color, flexShrink: 0, textTransform: 'capitalize' }}>{cat}</span>
                  </div>
                  {tmpl.description && (
                    <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>{tmpl.description.slice(0, 72)}{tmpl.description.length > 72 ? '…' : ''}</p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 10 }}>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{tmpl.blocks?.length || 0} blocks</span>
                    <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                      {tmpl.isStarter ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={e => handleUseStarter(e, tmpl._id, tmpl.name)}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7, border: 'none', background: `${color}22`, color, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            <Copy size={12} /> Use
                          </button>
                          <button onClick={e => { e.stopPropagation(); setCampaignTemplate(tmpl); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7, border: '1px solid rgba(255,20,147,0.3)', background: 'rgba(255,20,147,0.15)', color: '#FF1493', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                            title="Send Campaign">
                            Send
                          </button>
                        </div>
                      ) : (
                        <>
                          <button onClick={e => { e.stopPropagation(); navigate(`/editor/${tmpl._id}`); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7, border: 'none', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            <Edit2 size={12} /> Edit
                          </button>
                          <button onClick={e => { e.stopPropagation(); setCampaignTemplate(tmpl); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7, border: '1px solid rgba(255,20,147,0.3)', background: 'rgba(255,20,147,0.15)', color: '#FF1493', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                            title="Send Campaign">
                            Send
                          </button>
                          <button onClick={e => handleDelete(e, tmpl._id, tmpl.name)}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 7, border: 'none', background: 'rgba(255,20,20,0.1)', color: '#FF4444', fontSize: 11, cursor: 'pointer' }}>
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {campaignTemplate && (
        <CampaignDrawer
          template={campaignTemplate}
          onClose={() => setCampaignTemplate(null)}
          onSent={() => setCampaignTemplate(null)}
        />
      )}
    </div>
  );
};

export default TemplatesPage;
